/**
 * DH Announcement Bar
 *
 * Directional slide transition:
 *   Next / auto-advance → current exits left, new enters from right
 *   Previous            → current exits right, new enters from left
 *
 * Attributes (set via Liquid):
 *   data-rotation-speed    — ms between slides (default 4000)
 *   data-show-arrows-desktop — "true" | "false"
 *   data-show-arrows-mobile  — "true" | "false"
 *   data-show-close        — "true" | "false"
 *   data-dismiss-duration   — hours to hide after close (default 24)
 */
class DhAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isAnimating = false;
    this.interval = null;
    this.isPaused = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.dh-announcement__slide'));
    this.slideCount = this.slides.length;
    this.rotationSpeed = parseInt(this.dataset.rotationSpeed, 10) || 4000;
    this.dismissDuration = parseInt(this.dataset.dismissDuration, 10) || 24;
    this.transitionDuration = 300; // matches CSS --dh-ab-speed

    // Check if previously dismissed
    const dismissedUntil = localStorage.getItem('dh-announcement-dismissed');
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
      this.hidden = true;
      return;
    }

    // If no slides, bail
    if (this.slideCount === 0) return;

    // Activate first slide (no animation)
    this.slides[0].classList.add('is-active');

    // If only one slide, nothing else to do
    if (this.slideCount <= 1) return;

    // Start auto-rotation
    this.startRotation();

    // Arrow handlers
    this.querySelectorAll('.dh-announcement__arrow--prev').forEach((btn) => {
      btn.addEventListener('click', () => this.prev());
    });
    this.querySelectorAll('.dh-announcement__arrow--next').forEach((btn) => {
      btn.addEventListener('click', () => this.next());
    });

    // Close handler
    const closeBtn = this.querySelector('.dh-announcement__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.dismiss());
    }

    // Pause on hover (desktop)
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());

    // Touch swipe
    this.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
    this.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: true });
    this.addEventListener('touchend', () => this.onTouchEnd());

    // Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.detail.sectionId === this.dataset.sectionId) {
          this.stopRotation();
          this.connectedCallback();
        }
      });
    }
  }

  /**
   * Transition to a new slide with directional animation.
   * @param {number} nextIndex - Index of the slide to show
   * @param {'left'|'right'} direction - Direction content moves OUT
   *   'left'  = content exits left, new enters from right (next/auto)
   *   'right' = content exits right, new enters from left (prev)
   */
  goTo(nextIndex, direction) {
    if (this.isAnimating || nextIndex === this.currentIndex) return;
    this.isAnimating = true;

    const current = this.slides[this.currentIndex];
    const next = this.slides[nextIndex];
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Instant swap — no animation
      current.classList.remove('is-active');
      next.classList.add('is-active');
      this.currentIndex = nextIndex;
      this.isAnimating = false;
      return;
    }

    // Position the incoming slide at its starting point (off-screen)
    if (direction === 'left') {
      // Next: incoming starts at right (+100%), exits to left (-100%)
      next.style.transform = 'translateX(100%)';
      next.style.opacity = '0';
    } else {
      // Prev: incoming starts at left (-100%), exits to right (+100%)
      next.style.transform = 'translateX(-100%)';
      next.style.opacity = '0';
    }

    // Force reflow so the starting position is painted before transition begins
    next.getBoundingClientRect();

    // Remove static active state from current, add directional exit
    current.classList.remove('is-active');
    if (direction === 'left') {
      current.classList.add('is-exiting-left');
      next.classList.add('is-entering-right');
    } else {
      current.classList.add('is-exiting-right');
      next.classList.add('is-entering-left');
    }

    // Clear the inline starting position so the CSS transition classes take over
    next.style.transform = '';
    next.style.opacity = '';

    // After transition completes, clean up classes
    const onEnd = () => {
      current.classList.remove('is-exiting-left', 'is-exiting-right');
      next.classList.remove('is-entering-left', 'is-entering-right');
      next.classList.add('is-active');

      // Reset current slide position (off-screen right, default)
      current.style.transform = '';
      current.style.opacity = '';

      // Update aria
      current.setAttribute('aria-hidden', 'true');
      next.setAttribute('aria-hidden', 'false');

      this.currentIndex = nextIndex;
      this.isAnimating = false;
    };

    // Use transitionend with a safety timeout
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      next.removeEventListener('transitionend', handleTransitionEnd);
      onEnd();
    };

    const handleTransitionEnd = (e) => {
      if (e.target === next && e.propertyName === 'transform') {
        settle();
      }
    };

    next.addEventListener('transitionend', handleTransitionEnd);
    // Safety: if transitionend doesn't fire, settle after duration + buffer
    setTimeout(settle, this.transitionDuration + 50);
  }

  next() {
    if (this.isAnimating) return;
    const nextIndex = (this.currentIndex + 1) % this.slideCount;
    this.goTo(nextIndex, 'left');
    this.restartRotation();
  }

  prev() {
    if (this.isAnimating) return;
    const prevIndex = (this.currentIndex - 1 + this.slideCount) % this.slideCount;
    this.goTo(prevIndex, 'right');
    this.restartRotation();
  }

  startRotation() {
    if (this.slideCount <= 1) return;
    this.interval = setInterval(() => {
      if (!this.isPaused && !this.isAnimating) {
        const nextIndex = (this.currentIndex + 1) % this.slideCount;
        this.goTo(nextIndex, 'left');
      }
    }, this.rotationSpeed);
  }

  stopRotation() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  restartRotation() {
    this.stopRotation();
    this.startRotation();
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
  }

  dismiss() {
    const dismissUntil = Date.now() + this.dismissDuration * 60 * 60 * 1000;
    localStorage.setItem('dh-announcement-dismissed', dismissUntil.toString());

    // Animate the collapse
    this.classList.add('is-dismissing');
    requestAnimationFrame(() => {
      this.classList.add('is-dismissed');
    });

    // After animation, fully hide
    setTimeout(() => {
      this.hidden = true;
      this.classList.remove('is-dismissing', 'is-dismissed');
    }, 350);

    this.stopRotation();
  }

  onTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchEndX = this.touchStartX;
  }

  onTouchMove(e) {
    this.touchEndX = e.touches[0].clientX;
  }

  onTouchEnd() {
    const threshold = 40;
    const diff = this.touchEndX - this.touchStartX;

    if (Math.abs(diff) < threshold) return;

    if (diff < 0) {
      // Swiped left → show next (content moves left)
      this.next();
    } else {
      // Swiped right → show previous (content moves right)
      this.prev();
    }
  }

  disconnectedCallback() {
    this.stopRotation();
  }
}

customElements.define('dh-announcement-bar', DhAnnouncementBar);
